import type { AdminFlowToolbarProps } from "../../models/admin-flows.models";
import { ADMIN_FLOW_BUTTON, ADMIN_FLOW_INPUT, ADMIN_FLOW_PRIMARY } from "../../utils/admin-flows.constants";
import { EllipsisHorizontalIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef } from "react";

// Keep flow selection, presentation and primary actions in one compact row.
// Secondary editing and lifecycle details stay available without taking height away from the canvas.
const AdminFlowToolbar = ({
  flow,
  flows,
  toolCount,
  busy,
  persistent,
  canDelete,
  saveStatus,
  saving,
  onSelectFlow,
  onChangeName,
  onCreate,
  onSave,
  onDelete,
  onTest,
  onPublish,
}: AdminFlowToolbarProps) => {
  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const closeOutside = (event: PointerEvent) => {
      headerRef.current?.querySelectorAll<HTMLDetailsElement>("details[open]").forEach((details) => {
        if (!details.contains(event.target as Node)) details.open = false;
      });
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;

      headerRef.current?.querySelectorAll<HTMLDetailsElement>("details[open]").forEach((details) => {
        details.open = false;
        details.querySelector("summary")?.focus();
      });
    };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  return (
    <header
      ref={headerRef}
      data-test="admin-flow-toolbar"
      className="border-greyscale-20 flex shrink-0 flex-wrap items-center gap-2 border-b px-4 py-2"
    >
      <h1 className="mr-1 whitespace-nowrap text-lg font-semibold">AI Lab</h1>
      <label className="min-w-[10rem] flex-1 sm:max-w-[16rem]">
        <span className="sr-only">Working flow</span>
        <select
          data-test="admin-flow-select"
          className={`${ADMIN_FLOW_INPUT} !py-1.5`}
          value={flow.id}
          onChange={(event) => onSelectFlow(event.target.value)}
        >
          {flows.map((entry) => (
            <option key={entry.id} value={entry.id}>
              {entry.name} · {entry.status}
            </option>
          ))}
        </select>
      </label>
      <details className="relative" data-test="admin-flow-workspace-info">
        <summary
          className="cursor-pointer rounded bg-[#FFF5DE] px-2 py-1 text-xs text-[#725011]"
          title={persistent ? "Saved workspace details" : "Temporary workspace details"}
        >
          {persistent ? "Saved in DEV" : "Temporary"}
        </summary>
        <div className="border-greyscale-20 absolute left-0 top-full z-40 mt-2 w-72 max-w-[calc(100vw-2rem)] rounded border bg-white p-3 text-xs leading-5 shadow-lg dark:border-gray-600 dark:bg-gray-800">
          <p data-test="admin-flow-temporary-notice">
            {persistent
              ? "Saved drafts, Auto Builder messages and run history are retained in development Snowflake. Background schedules and email delivery are not active."
              : "Drafts and schedule settings reset when you leave or refresh. Run history resets when the backend restarts. Background schedules and email delivery are not active."}
          </p>
        </div>
      </details>
      <div className="ml-auto flex items-center gap-2">
        <span role="status" data-test="admin-flow-save-status" className="text-xs opacity-70">
          {saveStatus}
        </span>
        <button
          data-test="admin-flow-save"
          className={`${ADMIN_FLOW_BUTTON} !py-1.5`}
          disabled={saving}
          onClick={onSave}
        >
          {saving ? "Saving…" : "Save draft"}
        </button>
        <details key={flow.id} data-test="admin-flow-options" className="relative">
          <summary
            aria-label="Flow options"
            title="Flow options"
            data-test="admin-flow-options-toggle"
            className={`${ADMIN_FLOW_BUTTON} cursor-pointer list-none !px-2 !py-1.5 [&::-webkit-details-marker]:hidden`}
          >
            <EllipsisHorizontalIcon className="h-5 w-5" aria-hidden="true" />
          </summary>
          <div className="border-greyscale-20 absolute right-0 top-full z-40 mt-2 w-72 max-w-[calc(100vw-2rem)] space-y-3 rounded border bg-white p-4 shadow-lg dark:border-gray-600 dark:bg-gray-800">
            <label className="block space-y-1 text-xs" htmlFor="admin-flow-name">
              <span>Flow name</span>
              <input
                id="admin-flow-name"
                data-test="admin-flow-name"
                className={ADMIN_FLOW_INPUT}
                maxLength={120}
                value={flow.name}
                onChange={(event) => onChangeName(event.target.value)}
              />
            </label>
            <p className="text-xs leading-5 opacity-60">
              {flow.nodes.length} blocks · {flow.edges.length} connections · Revision {flow.revision} · {flow.status}
              <br />
              {toolCount} permitted tools · Shared across editors
            </p>
            <div className="flex flex-wrap gap-2">
              <button data-test="admin-flow-create" className={ADMIN_FLOW_BUTTON} onClick={onCreate}>
                + Create flow
              </button>
              {canDelete && (
                <button data-test="admin-flow-delete" className={ADMIN_FLOW_BUTTON} onClick={onDelete}>
                  Delete saved flow
                </button>
              )}
            </div>
          </div>
        </details>
        <button data-test="admin-flow-test" className={`${ADMIN_FLOW_BUTTON} !py-1.5`} disabled={busy} onClick={onTest}>
          {busy ? "Starting…" : "Test flow"}
        </button>
        <button data-test="admin-flow-publish" className={`${ADMIN_FLOW_PRIMARY} !py-1.5`} onClick={onPublish}>
          Publish
        </button>
      </div>
    </header>
  );
};

export default AdminFlowToolbar;
